import React, { useState, useEffect, useRef } from 'react';
import { FiHome, FiTrendingUp, FiCalendar, FiDollarSign, FiX } from 'react-icons/fi';
import Navigation from '../components/layout/Navigation';

const Portfolio: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('rankful');
  const [imageViewer, setImageViewer] = useState<{ src: string; alt: string } | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const screenshotsRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!screenshotsRef.current) return;
    setIsDragging(true);
    setHasDragged(false);
    setStartX(e.pageX - screenshotsRef.current.offsetLeft);
    setScrollLeft(screenshotsRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setHasDragged(false), 0);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !screenshotsRef.current) return;
    e.preventDefault();
    const x = e.pageX - screenshotsRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    if (Math.abs(walk) > 5) setHasDragged(true);
    screenshotsRef.current.scrollLeft = scrollLeft - walk;
  };

  const navigationItems = [
    {
      id: 'rankful',
      title: 'Rankful',
      color: 'rose',
      sections: [
        { id: 'rankful', label: 'Overview' },
        { id: 'rankful-features', label: 'Features' },
        { id: 'rankful-stack', label: 'Tech Stack' },
        { id: 'rankful-screens', label: 'Screenshots' }
      ]
    },
    {
      id: 'guess-nexus',
      title: 'Guess Nexus',
      color: 'blue',
      sections: [
        { id: 'guess-nexus', label: 'Overview' },
        { id: 'guess-nexus-features', label: 'Features' },
        { id: 'guess-nexus-stack', label: 'Tech Stack' },
        { id: 'guess-nexus-innovations', label: 'Innovations' }
      ]
    },
    {
      id: 'future-worth',
      title: 'FutureWorth',
      color: 'green',
      sections: [
        { id: 'future-worth', label: 'Overview' },
        { id: 'future-worth-features', label: 'Calculators' },
        { id: 'future-worth-stack', label: 'Tech Stack' },
        { id: 'future-worth-screens', label: 'Screenshots' }
      ]
    },
    {
      id: 'learn2',
      title: 'LEARN²',
      color: 'cyan',
      sections: [
        { id: 'learn2', label: 'Overview' },
        { id: 'learn2-features', label: 'Features' },
        { id: 'learn2-stack', label: 'Tech Stack' }
      ]
    },
    {
      id: 'travelloggy',
      title: 'TravelLoggy',
      color: 'teal',
      sections: [
        { id: 'travelloggy', label: 'Overview' },
        { id: 'travelloggy-features', label: 'Features' },
        { id: 'travelloggy-stack', label: 'Tech Stack' },
        { id: 'travelloggy-screens', label: 'Screenshots' }
      ]
    },
    {
      id: 'anthem-sigma',
      title: 'Anthem Sigma',
      color: 'purple',
      sections: [
        { id: 'anthem-sigma', label: 'Overview' },
        { id: 'anthem-sigma-features', label: 'Features' },
        { id: 'anthem-sigma-stack', label: 'Tech Details' },
        { id: 'anthem-sigma-highlights', label: 'Highlights' }
      ]
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const targetY = window.scrollY + window.innerHeight * 0.25;

      // Build a flat, ordered list of sections by their top positions
      const sectionIds = navigationItems.flatMap(p => p.sections.map(s => s.id));
      const sections = sectionIds
        .map(id => {
          const el = document.getElementById(id);
          if (!el) return null;
          const top = el.getBoundingClientRect().top + window.scrollY;
          return { id, top } as { id: string; top: number };
        })
        .filter(Boolean) as { id: string; top: number }[];

      if (sections.length === 0) return;

      // Ensure sorted by top (DOM order should already be sorted, but be safe)
      sections.sort((a, b) => a.top - b.top);

      // Use midpoints between adjacent section tops as boundaries so there are no gaps
      for (let i = 0; i < sections.length - 1; i++) {
        const boundary = (sections[i].top + sections[i + 1].top) / 2;
        if (targetY < boundary) {
          setActiveSection(sections[i].id);
          return;
        }
      }
      // If beyond last boundary, activate the last section
      setActiveSection(sections[sections.length - 1].id);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setImageViewer(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    const activeButton = sidebar?.querySelector(`[data-section-id="${activeSection}"]`) as HTMLElement;
    if (!sidebar || !activeButton) return;

    // If first section is active, scroll sidebar to very top
    if (activeSection === 'rankful') {
      sidebar.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const sidebarRect = sidebar.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const buffer = 120; // ~3 items worth of space

    const buttonTopRelative = buttonRect.top - sidebarRect.top;
    const buttonBottomRelative = buttonRect.bottom - sidebarRect.top;
    const sidebarHeight = sidebarRect.height;

    if (buttonTopRelative < buffer) {
      // Button is near the top - scroll up
      sidebar.scrollBy({ top: buttonTopRelative - buffer, behavior: 'smooth' });
    } else if (buttonBottomRelative > sidebarHeight - buffer) {
      // Button is near the bottom - scroll down
      sidebar.scrollBy({ top: buttonBottomRelative - sidebarHeight + buffer, behavior: 'smooth' });
    }
  }, [activeSection]);

  const getColorClasses = (color: string, isActive: boolean) => {
    const colorMap = {
      rose: isActive
        ? "text-rose-400 border-rose-500 bg-rose-500/10"
        : "text-rose-500 border-transparent hover:border-rose-500/50 hover:bg-rose-500/5",
      blue: isActive
        ? "text-blue-300 border-blue-400 bg-blue-400/10"
        : "text-blue-400 border-transparent hover:border-blue-400/50 hover:bg-blue-400/5",
      purple: isActive
        ? "text-purple-300 border-purple-400 bg-purple-400/10"
        : "text-purple-400 border-transparent hover:border-purple-400/50 hover:bg-purple-400/5",
      teal: isActive
        ? "text-teal-300 border-teal-400 bg-teal-400/10"
        : "text-teal-400 border-transparent hover:border-teal-400/50 hover:bg-teal-400/5",
      green: isActive
        ? "text-green-300 border-green-400 bg-green-400/10"
        : "text-green-400 border-transparent hover:border-green-400/50 hover:bg-green-400/5",
      cyan: isActive
        ? "text-cyan-300 border-cyan-400 bg-cyan-400/10"
        : "text-cyan-400 border-transparent hover:border-cyan-400/50 hover:bg-cyan-400/5"
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };
  const gameFeatures = [
    {
      title: "5 Daily Games",
      description: "Slay the Spire card guessing, Pokemon identification, Age of Empires II units, Subnautica creatures, and Bible books challenges",
      icon: (
        <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H7a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      )
    },
    {
      title: "User System",
      description: "Google OAuth integration, guest mode support, comprehensive achievement system, and competitive leaderboards",
      icon: (
        <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      )
    },
    {
      title: "Admin Dashboard",
      description: "Real-time analytics, user management, content moderation tools, and comprehensive activity tracking",
      icon: (
        <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    }
  ];

  const technicalHighlights = [
    {
      title: "Performance Optimized",
      description: "Strategic database indexing, connection pooling, and query optimization for scalability"
    },
    {
      title: "Data Integrity Practices",
      description: "Database migrations for version control, calculate-on-demand architecture preventing stale data, comprehensive validation systems"
    },
    {
      title: "Security Implementation",
      description: "JWT authentication with httpOnly cookies, OAuth integration, input sanitization, CORS configuration for controlled access"
    },
    {
      title: "Scalable Architecture",
      description: "Modular game system design, efficient caching strategies, connection pooling, RESTful API with consistent response formats"
    }
  ];


  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />

      {/* Sidebar Navigation */}
      <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:w-64">
        <div className="flex h-full flex-col bg-gray-800/50 backdrop-blur-sm border-r border-gray-700/50">
          <div ref={sidebarRef} className="flex flex-1 flex-col overflow-y-auto pt-20 pb-6 scrollbar-hide">
            <div className="px-6 py-4">
              <h2 className="text-lg font-semibold text-white mb-4">Projects</h2>
              <nav className="space-y-4">
                {navigationItems.map((project) => (
                  <div key={project.id} className="space-y-1">
                    <button
                      onClick={() => scrollToSection(project.id)}
                      className={`text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity duration-200 ${
                        project.sections.some(section => section.id === activeSection)
                          ? (project.color === 'rose' ? 'text-rose-400' : project.color === 'blue' ? 'text-blue-300' : project.color === 'purple' ? 'text-purple-300' : project.color === 'green' ? 'text-green-300' : project.color === 'cyan' ? 'text-cyan-300' : project.color === 'teal' ? 'text-teal-300' : 'text-blue-300')
                          : (project.color === 'rose' ? 'text-rose-500' : project.color === 'blue' ? 'text-blue-400' : project.color === 'purple' ? 'text-purple-400' : project.color === 'green' ? 'text-green-400' : project.color === 'cyan' ? 'text-cyan-400' : project.color === 'teal' ? 'text-teal-400' : 'text-blue-400')
                      }`}
                    >
                      {project.title}
                    </button>
                    <ul className="space-y-1">
                      {project.sections.map((section) => (
                        <li key={section.id}>
                          <button
                            data-section-id={section.id}
                            onClick={() => scrollToSection(section.id)}
                            className={`w-full text-left px-2 py-1.5 text-sm rounded-md border-l-2 transition-all duration-200 ${
                              getColorClasses(project.color, activeSection === section.id)
                            }`}
                          >
                            {section.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">

      {/* Rankful Hero Section */}
      <section id="rankful" className="bg-gray-900 pt-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              <span className="text-rose-500">Rankful</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              A multiplayer party ranking game where players take turns ranking items while others
              guess their preferences. Built with React Native and real-time Supabase backend for 2-20 players.
            </p>

            <div className="flex justify-center">
              <a
                href="https://apps.apple.com/us/app/rankful/id6757939531"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Download on App Store
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Rankful Key Features */}
      <section id="rankful-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A social party game that tests how well players know each other's preferences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Real-Time Sync</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Supabase WebSocket subscriptions with automatic polling fallback, ensuring reliable state sync across all connected players</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Gesture-Driven UI</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Drag-and-drop ranking built with React Native Reanimated and Gesture Handler for fluid 60fps interactions and haptic feedback</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Server-Authoritative Timing</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Server time synchronization prevents cheating, with phase-based game state management and graceful reconnection handling</p>
            </div>
          </div>
        </div>
      </section>

      {/* Rankful Technical Stack */}
      <section id="rankful-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Stack</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Modern mobile architecture with real-time backend capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-rose-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Mobile App
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  React Native with Expo SDK 54
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  TypeScript for type-safe development
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Custom hooks architecture
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Reanimated + Gesture Handler for fluid interactions
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-rose-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Backend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Supabase (PostgreSQL + Realtime)
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Real-time subscriptions with polling fallback
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Anonymous and email authentication
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-rose-500 rounded-full mr-3"></span>
                  Server time sync for fair gameplay
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Rankful Screenshots */}
      <section id="rankful-screens" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Screenshots</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Scroll through the app experience
            </p>
          </div>

          <div
            ref={screenshotsRef}
            className={`overflow-x-auto pb-4 scrollbar-thin ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            <div className="flex gap-6 min-w-max px-4">
              {[1, 2, 4, 3, 5, 6].map((num) => (
                <div
                  key={num}
                  className={`flex-shrink-0 bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-rose-500/40 transition-all duration-200 select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => !hasDragged && setImageViewer({src: `/rankful/${num}.png`, alt: `Rankful Screenshot ${num}`})}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src: `/rankful/${num}.png`, alt: `Rankful Screenshot ${num}`})}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <img
                    src={`/rankful/${num}.png`}
                    alt={`Rankful Screenshot ${num}`}
                    className="h-[550px] w-auto object-contain pointer-events-none"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>

      {/* Guess Nexus Hero Section */}
      <section id="guess-nexus" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Guess <span className="text-blue-400">Nexus</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              A daily puzzle gaming platform featuring five unique games inspired by popular franchises,
              complete with user authentication, achievements, and comprehensive analytics.
            </p>

            <div className="flex justify-center">
              <a
                href="https://guessnexus.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Try It Out
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section id="guess-nexus-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A comprehensive gaming platform with multiple puzzle games, user management, and administrative tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {gameFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
                <div className="flex items-center mb-4">
                  {feature.icon}
                  <h3 className="text-xl font-semibold text-white ml-3">{feature.title}</h3>
                </div>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Stack */}
      <section id="guess-nexus-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Stack</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built with modern technologies and best practices for performance, scalability, and maintainability
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Frontend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  React 18 with functional components and hooks
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  React Router DOM v7 for navigation
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  SCSS Modules with BEM naming convention
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  Context API for state management
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  Custom hooks for game logic and API interactions
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Backend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  Node.js with Express.js framework
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  PostgreSQL database with migrations
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  JWT Authentication with httpOnly cookies
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  Passport.js for Google OAuth integration
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>
                  RESTful API with comprehensive error handling
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Highlights */}
      <section id="guess-nexus-innovations" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Innovations</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Advanced solutions to complex problems showcasing engineering excellence
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {technicalHighlights.map((highlight, index) => (
              <div key={index} className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                <h3 className="text-xl font-semibold text-blue-400 mb-3">{highlight.title}</h3>
                <p className="text-gray-300 leading-relaxed">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>


      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>

      {/* FutureWorth Hero Section */}
      <section id="future-worth" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Future <span className="text-green-400">Worth</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              Focused, high-performance financial calculators for mortgages, compound growth,
              retirement scenarios, and loan payoff strategies — with responsive charts and full schedules.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/dpesall/Future-Worth"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                View Code
              </a>
              <a
                href="#future-worth-screens"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold text-lg border border-gray-700 hover:border-gray-600 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M4 6h8M4 10h8M4 14h8M4 18h8" />
                </svg>
                View Screenshots
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FutureWorth Calculators */}
      <section id="future-worth-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Calculators</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore mortgage payments, compound growth, retirement scenarios, and loan payoff strategies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <FiHome className="w-8 h-8 text-green-400" />
                <h3 className="text-xl font-semibold text-white ml-3">Mortgage</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Estimate P&I, escrow, PMI drop‑off, payoff date, and view a full amortization schedule.</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <FiTrendingUp className="w-8 h-8 text-green-400" />
                <h3 className="text-xl font-semibold text-white ml-3">Compound Interest</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Project future value with contributions, annual increases, compounding cadence, and inflation adjustments.</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <FiCalendar className="w-8 h-8 text-green-400" />
                <h3 className="text-xl font-semibold text-white ml-3">Retirement</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Model accumulation and drawdown with inflation‑adjusted income and depletion visibility.</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <FiDollarSign className="w-8 h-8 text-green-400" />
                <h3 className="text-xl font-semibold text-white ml-3">Loan Payoff</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Compare strategies with extra payments and fees, showing payoff date and interest saved.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FutureWorth Tech Stack */}
      <section id="future-worth-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Stack</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Built with modern React, responsive charts, and clean SCSS architecture
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Frontend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  React 18 with React Router
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  JavaScript (ESNext)
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  Component-driven architecture with custom hooks
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Charts & Styling
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  Recharts for responsive data visualization
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  Sass (SCSS) with strict per‑component BEM
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-green-400 rounded-full mr-3"></span>
                  Accessibility and responsive layout best practices
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FutureWorth Screenshots */}
      <section id="future-worth-screens" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Screenshots</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A few highlights from the calculators and home experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/fw-home-page.png', alt:'FutureWorth Home'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/fw-home-page.png', alt:'FutureWorth Home'})}
            >
              <img src="/fw-home-page.png" alt="FutureWorth Home" className="w-full h-56 object-cover" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">Home</p>
              </div>
            </div>

            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/fw-compound-interest.jpeg', alt:'Compound Interest Calculator'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/fw-compound-interest.jpeg', alt:'Compound Interest Calculator'})}
            >
              <img src="/fw-compound-interest.jpeg" alt="Compound Interest Calculator" className="w-full h-56 object-cover" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">Compound Interest</p>
              </div>
            </div>

            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/fw-mortgage.jpeg', alt:'Mortgage Calculator'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/fw-mortgage.jpeg', alt:'Mortgage Calculator'})}
            >
              <img src="/fw-mortgage.jpeg" alt="Mortgage Calculator" className="w-full h-56 object-cover" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">Mortgage</p>
              </div>
            </div>

            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/fw-retirement.jpeg', alt:'Retirement Savings Calculator'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/fw-retirement.jpeg', alt:'Retirement Savings Calculator'})}
            >
              <img src="/fw-retirement.jpeg" alt="Retirement Savings Calculator" className="w-full h-56 object-cover" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">Retirement Savings</p>
              </div>
            </div>

            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/fw-loan-payoff.jpeg', alt:'Loan Payoff Calculator'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/fw-loan-payoff.jpeg', alt:'Loan Payoff Calculator'})}
            >
              <img src="/fw-loan-payoff.jpeg" alt="Loan Payoff Calculator" className="w-full h-56 object-cover" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">Loan Payoff</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>

      {/* LEARN2 (ordered after FutureWorth) */}
      <section id="learn2" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              LEARN<span className="text-blue-400 align-text-top">²</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-4xl mx-auto">
              Front-end for the Landfalling Event Atmospheric River Neural Network (LEARN²):
              a decision-support tool delivering extreme rainfall predictions up to 10 days out
              using ensemble NWP guidance, remotely-sensed fields, teleconnection indices, and
              neural networks with a voting mechanism.
            </p>

            <div className="flex justify-center">
              <a
                href="https://github.com/dpesall/Learn2"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                View Code
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="learn2-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Forecast visualization and data access for extreme rainfall prediction across 1–10 day horizons
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <h3 className="text-xl font-semibold text-white mb-3">Ensemble-driven Predictions</h3>
              <p className="text-gray-300">Leverages GEFS and ECMWF ensembles with a neural “voting” approach to identify threshold exceedance.</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <h3 className="text-xl font-semibold text-white mb-3">Multiple Threshold Modes</h3>
              <p className="text-gray-300">Switch between percentile (50th–99th) and static inch thresholds (0.25–4.0) for 10 look‑ahead days.</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <h3 className="text-xl font-semibold text-white mb-3">Data Access APIs</h3>
              <p className="text-gray-300">Signed S3 image URLs and CSV retrieval endpoints back the UI for rapid per‑day exploration.</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <h3 className="text-xl font-semibold text-white mb-3">Operational Focus</h3>
              <p className="text-gray-300">Designed to aid meteorologists and hydrologists in medium‑range decision windows with higher confidence.</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <h3 className="text-xl font-semibold text-white mb-3">User Feedback</h3>
              <p className="text-gray-300">Built‑in feedback submission via Outlook email integration.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="learn2-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Stack</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Node.js + Express server powering a static front‑end with AWS S3 integrations
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Backend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>Node.js with Express</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>AWS SDK (S3 signed URLs, object listing)</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>fast-csv for CSV parsing</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>Nodemailer for feedback handling</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>dotenv for configuration</li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-blue-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Frontend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>Static HTML/CSS with Bootstrap</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>Interactive UI backed by S3 endpoints</li>
                <li className="flex items-center"><span className="w-2 h-2 bg-blue-400 rounded-full mr-3"></span>Deployed with HTTP/HTTPS server modes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>

      {/* TravelLoggy Hero Section */}
      <section id="travelloggy" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Travel<span className="text-teal-400">Loggy</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              An interactive travel map application for tracking visited countries and US states.
              Mark regions as visited, want to visit, lived there, or never want to go — with beautiful
              color-coded maps and real-time statistics.
            </p>

            <div className="flex justify-center">
              <a
                href="#travelloggy-screens"
                className="inline-flex items-center gap-2 px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                View Screenshots
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TravelLoggy Features */}
      <section id="travelloggy-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Track your travels with an intuitive map interface and detailed statistics
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Interactive Maps</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Smooth MapLibre GL JS rendering with click-to-select regions, hover highlights, and paint mode for rapid status assignment</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Fun to Explore</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">A satisfying way to visualize your travel history — show off where you've lived, places you've visited, bucket list destinations, and spots you're avoiding</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">5 Travel Statuses</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Been There, Want to Go, Lived There, Never Been, and Don't Want to Go — each with distinct colors for clear visualization</p>
            </div>
          </div>
        </div>
      </section>

      {/* TravelLoggy Tech Stack */}
      <section id="travelloggy-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Stack</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Modern React with MapLibre for high-performance map rendering
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-teal-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Frontend
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  React 19 with Vite
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  MapLibre GL JS for map rendering
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  SCSS with BEM naming convention
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  React Context for state management
                </li>
              </ul>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50">
              <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                <svg className="w-6 h-6 text-teal-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Map Features
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  GeoJSON country and state boundaries
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  Dynamic label positioning with offsets
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  Compass, zoom indicator, and label toggle
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-teal-400 rounded-full mr-3"></span>
                  localStorage persistence for guest data
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TravelLoggy Screenshots */}
      <section id="travelloggy-screens" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Screenshots</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              World and US States map views with travel status tracking
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-teal-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/travelloggy-screenshot.jpeg', alt:'TravelLoggy US States Map'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/travelloggy-screenshot.jpeg', alt:'TravelLoggy US States Map'})}
            >
              <img src="/travelloggy-screenshot.jpeg" alt="TravelLoggy US States Map" className="w-full h-auto object-contain" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">US States View</p>
              </div>
            </div>
            <div
              className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-700/50 hover:border-teal-400/40 transition-all duration-200 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => setImageViewer({src:'/travelloggy-screenshot-2.jpeg', alt:'TravelLoggy World Map'})}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setImageViewer({src:'/travelloggy-screenshot-2.jpeg', alt:'TravelLoggy World Map'})}
            >
              <img src="/travelloggy-screenshot-2.jpeg" alt="TravelLoggy World Map" className="w-full h-auto object-contain" />
              <div className="p-4 border-t border-gray-700/50">
                <p className="text-sm text-gray-300">World View</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Divider */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-700/50"></div>
        </div>
      </section>

      {/* Anthem Sigma (ordered last) */}
      <section id="anthem-sigma" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Anthem <span className="text-purple-400">Sigma</span>
            </h1>
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              A comprehensive cryptography education tool implementing classical cipher algorithms
              for learning, analysis, and recreational use.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://github.com/dpesall/Anthem-Sigma"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold text-lg transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                View Code
              </a>
              <a
                href="https://github.com/dpesall/Anthem-Sigma/releases"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-semibold text-lg border border-gray-700 hover:border-gray-600 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download App
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="anthem-sigma-features" className="bg-gray-800/50 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Features</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive cryptography toolkit with cipher implementations and analysis tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">5 Classical Ciphers</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Caesar, Keyword, Affine, Vigenère, and Hill cipher implementations with customizable parameters and mathematical transformations</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Cryptanalysis Tools</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">Letter frequency analysis, N-gram pattern analysis, and monospace display for examining cipher text and breaking unknown encryptions</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253" />
                </svg>
                <h3 className="text-xl font-semibold text-white ml-3">Educational Interface</h3>
              </div>
              <p className="text-gray-300 leading-relaxed">File upload support, real-time validation, multi-page navigation, and built-in documentation for comprehensive learning experience</p>
            </div>
          </div>
        </div>
      </section>

      <section id="anthem-sigma-stack" className="bg-gray-900 py-20 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Details</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Windows desktop application built with modern .NET technologies
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                  <svg className="w-6 h-6 text-purple-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Platform & Technology
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    Windows Desktop Application
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    C# with Windows Forms
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    .NET Framework 4.7.2
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    UTF-8 file encoding support
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
                  <svg className="w-6 h-6 text-purple-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253" />
                  </svg>
                  Educational Purpose
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    Cryptography students and educators
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    Computer science curriculum support
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    Puzzle enthusiasts and hobbyists
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    Security awareness training
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="anthem-sigma-highlights" className="bg-gray-800/50 py-20 pb-48 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Technical Highlights</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Showcasing desktop application development and cryptographic algorithm implementation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-semibold text-purple-400 mb-3">Algorithm Implementation</h3>
              <p className="text-gray-300 leading-relaxed">Five different cipher methods with proper mathematical transformations, matrix operations, and modular arithmetic calculations</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-semibold text-purple-400 mb-3">Analysis Capabilities</h3>
              <p className="text-gray-300 leading-relaxed">Statistical analysis tools for cryptographic pattern recognition, frequency distribution, and N-gram pattern detection</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-semibold text-purple-400 mb-3">User Experience Design</h3>
              <p className="text-gray-300 leading-relaxed">Intuitive Windows Forms interface with real-time validation, file processing capabilities, and organized multi-page navigation</p>
            </div>

            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
              <h3 className="text-xl font-semibold text-purple-400 mb-3">Educational Focus</h3>
              <p className="text-gray-300 leading-relaxed">Built-in documentation, usage instructions, and educational content designed specifically for learning cryptographic concepts</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal for Screenshots */}
      {imageViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setImageViewer(null)} />
          <div className="relative z-10 inline-block max-w-[92vw] md:max-w-[84vw]">
            <button
              aria-label="Close image viewer"
              onClick={() => setImageViewer(null)}
              className="absolute -top-3 -right-3 bg-gray-800 text-white rounded-full p-2 shadow-lg border border-gray-700 hover:bg-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
            <img
              src={imageViewer.src}
              alt={imageViewer.alt}
              className="max-w-[92vw] md:max-w-[84vw] max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl border border-gray-700"
            />
          </div>
        </div>
      )}
      

      </div> {/* End of Main Content */}
    </div>
  );
};

export default Portfolio;
